// AdminEarlyAccess component for managing Sequoia Green AI early access signups
import React from 'react';

function AdminEarlyAccess({ supabaseClient }) {
    const [signups, setSignups] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [exportStatus, setExportStatus] = React.useState('');

    React.useEffect(() => {
        fetchSignups();
    }, []);

    const fetchSignups = async () => {
        try {
            setIsLoading(true);
            setError(null);
            
            // Check if the signupService exists and has the getEarlyAccessSignups method
            if (!window.signupService || typeof window.signupService.getEarlyAccessSignups !== 'function') {
                throw new Error('Signup service not available');
            }
            
            const data = await window.signupService.getEarlyAccessSignups();
            setSignups(data);
        } catch (err) {
            console.error('Error fetching early access signups:', err);
            setError(err.message || 'Failed to load early access signups');
        } finally {
            setIsLoading(false);
        }
    };

    const handleExportCSV = async () => {
        try {
            setExportStatus('Generating CSV...');
            
            if (!window.signupService || typeof window.signupService.exportEarlyAccessSignupsAsCSV !== 'function') {
                throw new Error('Export functionality not available');
            }
            
            const csvContent = await window.signupService.exportEarlyAccessSignupsAsCSV();
            
            // Create a download link
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', 'sequoia_early_access_signups.csv');
            document.body.appendChild(link);
            
            // Trigger download and cleanup
            link.click();
            document.body.removeChild(link);
            
            setExportStatus('Export complete!');
            setTimeout(() => setExportStatus(''), 3000);
        } catch (err) {
            console.error('Error exporting signups:', err);
            setExportStatus(`Export failed: ${err.message}`);
            setTimeout(() => setExportStatus(''), 5000);
        }
    };

    // Check if the early_access_signups table exists
    const checkAndCreateTable = async () => {
        try {
            // First try to query the table
            const { error } = await supabaseClient
                .from('early_access_signups')
                .select('count')
                .limit(1);

            // If there's an error that indicates the table doesn't exist
            if (error && error.code === '42P01') {
                setError("The early_access_signups table doesn't exist. Creating it now...");
                
                // Create the table using SQL
                const { error: createError } = await supabaseClient.rpc(
                    'execute_sql',
                    { 
                        query: `
                            CREATE TABLE IF NOT EXISTS early_access_signups (
                                id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
                                email TEXT UNIQUE NOT NULL,
                                created_at TIMESTAMP WITH TIME ZONE DEFAULT CURRENT_TIMESTAMP
                            );
                        `
                    }
                );

                if (createError) {
                    throw new Error(`Failed to create table: ${createError.message}`);
                }

                setError("Table created successfully! Refreshing data...");
                setTimeout(fetchSignups, 1000);
            }
        } catch (err) {
            console.error('Error checking/creating table:', err);
            setError(`Database issue: ${err.message}`);
        }
    };

    // If no signups and there's an error, offer to create the table
    React.useEffect(() => {
        if (error && signups.length === 0 && !isLoading && supabaseClient) {
            checkAndCreateTable();
        }
    }, [error, signups, isLoading]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Sequoia Green AI Early Access Signups</h2>
                <div className="flex space-x-2">
                    <button 
                        onClick={fetchSignups}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                    >
                        <i className="fas fa-sync-alt mr-2"></i> Refresh
                    </button>
                    <button 
                        onClick={handleExportCSV}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center"
                        disabled={signups.length === 0}
                    >
                        <i className="fas fa-file-csv mr-2"></i> Export CSV
                    </button>
                </div>
            </div>

            {error && (
                <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg">
                    <p className="flex items-center">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        {error}
                    </p>
                </div>
            )}

            {exportStatus && (
                <div className="mb-6 p-4 bg-green-100 text-green-700 rounded-lg">
                    <p className="flex items-center">
                        <i className="fas fa-info-circle mr-2"></i>
                        {exportStatus}
                    </p>
                </div>
            )}

            {signups.length > 0 ? (
                <div className="bg-white shadow-md rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Email
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Signup Date
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {signups.map((signup, index) => (
                                <tr key={signup.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {signup.email}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(signup.created_at).toLocaleDateString()} {new Date(signup.created_at).toLocaleTimeString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="bg-gray-50 p-8 text-center rounded-lg border border-gray-200">
                    <div className="text-4xl text-gray-300 mb-4">
                        <i className="fas fa-inbox"></i>
                    </div>
                    <h3 className="text-lg font-medium text-gray-600 mb-1">No Signups Yet</h3>
                    <p className="text-gray-500">
                        There are no early access signups for Sequoia Green AI yet.
                    </p>
                </div>
            )}
        </div>
    );
}

window.AdminEarlyAccess = AdminEarlyAccess;
export default AdminEarlyAccess;